<template>
  <content-dialog
    :visible.sync="modalVisible"
    max-width="1200px"
    :scrollable="true"
    card-class="blue-grey lighten-5"
    small-title
    fullscreen
    force-close-icon
  >
    <template slot="dialogTitle">
      <v-skeleton-loader v-if="!yachtEquipmentPack" />
      <div v-else>
        <div>
          {{ yachtEquipmentPack.label }}
        </div>
        <div class="body-2">
          <span class="grey--text">Public Label</span>: {{ yachtEquipmentPack.public_label }}
        </div>
      </div>
    </template>

    <v-card-text
      v-if="yachtEquipmentPack"
      class="pa-0"
    >
      <v-row
        class="fill-height"
        no-gutters
      >
        <v-col
          lg="10"
          class="pa-6 fill-height"
          style="overflow-y: auto"
        >
          <v-card
            elevation="10"
            rounded
          >
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>
                    Item
                  </th>
                  <th>
                    Code
                  </th>
                  <th>
                    Quantity
                  </th>
                  <th>
                    Purchase
                  </th>
                  <th>
                    Sale
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) in yachtEquipmentPack.items"
                  :key="'item_'+index"
                >
                  <td>
                    {{ item.label }}
                  </td>
                  <td>
                    {{ item.code }}
                  </td>
                  <td>
                    {{ item.quantity }}
                  </td>
                  <td class="text-no-wrap">
                    {{ item.unit_purchase_price | currency(yachtEquipmentPack.currency) }}
                  </td>
                  <td class="text-no-wrap">
                    {{ item.unit_sale_price | currency(yachtEquipmentPack.currency) }}
                  </td>
                </tr>
                <tr>
                  <td
                    colspan="3"
                    class="font-weight-bold"
                  >
                    Total
                  </td>
                  <td class="font-weight-bold primary--text text-no-wrap">
                    {{ yachtEquipmentPack.purchase_price | currency(yachtEquipmentPack.currency) }}
                  </td>
                  <td class="font-weight-bold primary--text text-no-wrap">
                    {{ yachtEquipmentPack.sale_price | currency(yachtEquipmentPack.currency) }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
        <!--<editor-fold desc="Price Column">-->
        <v-col
          lg="6"
          class="pa-6 fill-height"
          style="overflow-y: auto"
        >
          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <v-subheader>
                Status
              </v-subheader>
            </v-col>
            <v-col cols="auto">
              <v-chip
                small
                :color="statusData[yachtEquipmentPack.status].color"
                :dark="statusData[yachtEquipmentPack.status].dark || false"
              >
                {{ statusData[yachtEquipmentPack.status].label }}
              </v-chip>
            </v-col>
          </v-row>

          <v-divider />

          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <v-subheader>
                Code
              </v-subheader>
            </v-col>
            <v-col cols="auto">
              <span v-if="yachtEquipmentPack.code">
                {{ yachtEquipmentPack.code }}
              </span>
              <span
                v-else
                class="grey--text"
              >n/a</span>
            </v-col>
          </v-row>

          <v-divider />

          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <v-subheader>
                Installed By
              </v-subheader>
            </v-col>
            <v-col cols="auto">
              {{ installedByData[yachtEquipmentPack.installed_by].label }}
            </v-col>
          </v-row>

          <v-divider />

          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <v-subheader>
                Countries Available
              </v-subheader>
            </v-col>
            <v-col cols="auto">
              <flag
                v-for="country in countries"
                :key="country.id"
                :iso="country.code"
                :squared="false"
                class="radius-3 mr-1 elevation-5"
                :style="[ ...!country.included && { opacity: 0.2, filter: 'grayscale(100%)' } ]"
              />
            </v-col>
          </v-row>

          <v-divider />

          <v-subheader>
            Yacht Models Applicable
          </v-subheader>
          <v-card
            rounded
            elevation="10"
          >
            <v-row
              v-for="model in yachtEquipmentPack.models"
              :key="model.id"
              no-gutters
              class="py-2 px-3 list--bordered body-2"
            >
              <v-col>
                {{ model.model_year }} {{ model.full_label }}
              </v-col>
              <v-col cols="auto">
                <span
                  v-if="model.default"
                  class="primary--text font-weight-bold caption"
                >
                  Default
                </span>
                <span
                  v-else
                  class="grey--text caption"
                >
                  Optional
                </span>
              </v-col>
            </v-row>
          </v-card>

          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <v-subheader>
                Last Update At
              </v-subheader>
            </v-col>
            <v-col cols="auto">
              {{ yachtEquipmentPack.updated_at | moment('D MMM YYYY HH:mm:ss') }}
            </v-col>
          </v-row>

          <v-divider />

          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <v-subheader>
                First Created At
              </v-subheader>
            </v-col>
            <v-col cols="auto">
              {{ yachtEquipmentPack.created_at | moment('D MMM YYYY HH:mm:ss') }}
            </v-col>
          </v-row>
        </v-col>
        <!--</editor-fold>-->
      </v-row>
    </v-card-text>

    <yacht-equipment-pack-edit-dialog
      v-if="yachtEquipmentPack"
      :yacht-equipment-pack="copyPack || yachtEquipmentPack"
      :visible.sync="editDialog"
      @yachtEquipmentPack:created="fetchPack(); $emit('yacht-equipment-pack:created', $event)"
      @yachtEquipmentPack:updated="fetchPack(); $emit('yacht-equipment-pack:updated', $event)"
      @yachtEquipmentPack:deleted="fetchPack(); $emit('yacht-equipment-pack:deleted', yachtEquipmentPack)"
    />

    <template slot="dialogActions">
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Close
      </v-btn>
      <v-btn
        :disabled="!yachtEquipmentPack"
        color="primary"
        @click="doCopyPack()"
      >
        Copy Pack
      </v-btn>
      <v-btn
        :disabled="!yachtEquipmentPack"
        color="primary"
        @click="copyPack = undefined; editDialog = true"
      >
        Edit Pack
      </v-btn>
    </template>
  </content-dialog>
</template>

<script>
import ContentDialog from '@/components/helpers/Dialog'
import YachtEquipmentPack from '@/models/YachtEquipmentPack'
import { cloneDeep, sortBy } from 'lodash'
import YachtEquipmentPackEditDialog from '@/components/settings/yachtEquipmentPacks/YachtEquipmentPackEditDialog'

export default {
  components: {
    YachtEquipmentPackEditDialog,
    ContentDialog
  },
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    yachtEquipmentPackId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      yachtEquipmentPack: undefined,
      copyPack: undefined,
      editDialog: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    countries() {
      return sortBy(this.$store.getters['settings/charteringCountries'].data, 'name').map(c => {
        return {
          ...c,
          included: this.yachtEquipmentPack.countries.some(pc => c.id === pc.id)
        }
      })
    }
  },
  watch: {
    modalVisible: function (value) {
      if (!value) return

        this.fetchPack()
    }
  },
  created() {
    this.installedByData = YachtEquipmentPack.installedByData
    this.statusData = YachtEquipmentPack.statusData

      this.fetchPack()
  },
  methods: {
    async fetchPack() {
      await this.$nextTick()

      this.yachtEquipmentPack = await YachtEquipmentPack.api.find(this.yachtEquipmentPackId)
    },
    doCopyPack() {
      this.copyPack = cloneDeep(this.yachtEquipmentPack)
      this.copyPack.id = undefined
      this.editDialog = true
    }
  }
}
</script>
